import { STEER_MAX } from '../vehicles/carSpec.js';

/**
 * @file THE PLAYER'S CAR. Press F beside a parked one to get in, F again to get
 * out, and in between the car is a real body with a real handbrake.
 *
 * Like `traffic/Traffic.js` this imports no three.js: the fleet is reached
 * through the director, the player through its own tiny kinematic hook, and the
 * camera through `CameraRig`. That is what lets `tests/traffic.test.mjs` drive
 * a car round a circle and assert the radius.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE PLAYER'S CAR IS THE ONLY CAR WITH PHYSICS
 * ─────────────────────────────────────────────────────────────────────────────
 * An AI car is an arc length on a lane and cannot leave it (see `Traffic.js`).
 * This one is a bicycle with a TRACTION BUDGET, and the budget is the whole
 * model. The target is grounded arcade — GTA IV rather than a tyre simulator:
 * noticeable mass, a turn radius that opens up hard with speed, understeer when
 * you throw it in too fast, brakes that tighten the line, and a handbrake that
 * still throws the back out.
 *
 * It replaced a pure kinematic bicycle whose only speed cue was a LINEAR
 * steering-lock ramp, and whose lateral velocity was bled at a flat `grip` per
 * second. That car turned at ~1 rad/s at every speed from 6 m/s to 17 m/s
 * (radius 7.4 m → 18.9 m, i.e. barely moving across the whole driveable band),
 * pulled 1.5 g at top speed without consequence, and rotated its velocity
 * vector with its heading so hard it read as a tank turret rather than a car —
 * which is exactly what "a fixed turn radius no matter the speed" describes
 * from the driver's seat (owner playtest round 3).
 *
 * FIVE PARTS, in the order the step runs them:
 *
 *  1. **WEIGHT TRANSFER** is one smoothed state byte, {@link CarBody.load}:
 *     +1 nose down on the brakes, −1 tail squatted on the power, chasing its
 *     target at {@link DRIVE.loadRate}. It is what makes the pedals change the
 *     CAR rather than just its speed, and the lag on it is the "you feel the
 *     mass" cue — instantaneous load transfer reads as a switch, not a body.
 *  2. **THE GRIP BUDGET** is {@link gripAt} m/s² of lateral tyre force —
 *     {@link DRIVE.gripLat} up to `v*`, then rising by {@link DRIVE.gripGain}
 *     toward the limiter — scaled UP by the load on the brakes
 *     ({@link DRIVE.brakeGrip}), DOWN on the power ({@link DRIVE.powerLoss}),
 *     and down again by however much the tyres are already spending holding a
 *     slide ({@link DRIVE.slideLoss}). The speed term is arcade forgiveness and
 *     says so at its definition; the other three are the model.
 *  3. **THE LOCK** is derived from the budget, not tabled against it: full lock
 *     is the angle whose geometry demands {@link DRIVE.lockAuthority} times the
 *     BASE budget at this speed, so it falls as 1/v² — the curve, not the
 *     straight line the old model used. Deriving it from the base rather than
 *     the live budget is the trick that makes part 4 visible.
 *  4. **THE YAW IS CAPPED BY THE BUDGET.** `yaw' = v/wb·tan(δ)` is only what
 *     the geometry ASKS for; what it gets is clamped to `aLat / v`, the fastest
 *     the tyres can bend the path at this speed. Above ~7 m/s the clamp is
 *     always the binding constraint, so the steady radius is `v² / aLat` —
 *     20 m at the limiter against 7 m at walking pace, i.e. still most of a
 *     square law but no longer the 30 m circle a constant budget gives.
 *     Turning in harder than the clamp allows does nothing — that is
 *     understeer, and it is why braking (a bigger budget) tightens the line and
 *     flooring it (a smaller one) washes wide.
 *  5. **THE LATERAL BLEED** still pulls the velocity back onto the heading, but
 *     it is now capped at {@link DRIVE.gripRecover} m/s² instead of being a
 *     flat exponential. The old uncapped bleed could scrub 5 g off a sideways
 *     car in a step; with the cap a real slide takes real time to gather up.
 *
 * Five details that are easy to get subtly wrong:
 *  • the longitudinal impulse is applied along the OLD heading and the grip
 *    against the NEW one. Decomposing and recomposing against the same heading
 *    rotates the velocity vector with the car for free, i.e. infinite grip, and
 *    the handbrake then does nothing;
 *  • the yaw clamp is a PATH-CURVATURE limit, so on its own it forbids the one
 *    thing a drift is (heading rotating faster than the path). The handbrake
 *    lifts it by {@link DRIVE.handbrakeYaw} at the same moment it drops the
 *    lateral bleed to {@link DRIVE.handbrakeGrip} — those two together ARE the
 *    slide, and they are what keeps it distinct from losing grip naturally,
 *    which is understeer (the car pushes wide, nose still pointing where you
 *    aimed it);
 *  • `lockAuthority` above 1 is deliberate: full lock asks for slightly MORE
 *    than the tyres have, so holding it flat out always understeers a little.
 *    Below 1 the car can never break traction with steering alone and the
 *    budget stops mattering; far above 1 the top half of the stick is dead;
 *  • the steering RATE falls with speed too ({@link DRIVE.steerRate} at rest to
 *    {@link DRIVE.steerRateTop} flat out) but UNWINDING is always fast, because
 *    a slow counter-steer makes a slide unsaveable;
 *  • yaw uses the vehicle stack's convention — `Ry(yaw)` maps the nose (+X) to
 *    `(cos yaw, −sin yaw)`, so a POSITIVE steer turns LEFT (toward −Z, the
 *    driver's side). A key bound the other way feels like a mirror.
 *
 * {@link CarBody.steerAngle} is the angle the model ACTUALLY used, in radians,
 * and it is what the fleet's front wheels are driven from — not the raw
 * `steer` command. At 17 m/s the lock is ~22%, so wheels cranked to the stop
 * while the car takes a 30 m arc would read as the sim not driving the art.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ENTER / EXIT IS A TWO-PHASE SEAM
 * ─────────────────────────────────────────────────────────────────────────────
 * {@link createDriving}'s `enterCar` REQUESTS the transfer and `finishEnter`
 * completes it; the gap between them is the animation, and the DEFAULT gap is
 * still {@link DRIVE.enterTime} = 0 (request and completion back to back) for
 * any caller that has no animation to play. `systems/Carjack.js` passes its
 * own duration instead, so the door swing, the drag and the slide-in are one
 * table of timings in one file rather than half a table here. In between, the
 * car is already handed over — nobody else can take it — and the player is not
 * yet driving it. `exitCar` is the same shape. Nothing outside this file may
 * set the driving state directly.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * BAILING OUT: THE COAST
 * ─────────────────────────────────────────────────────────────────────────────
 * There used to be a speed gate on `exitCar` — you could not step out above
 * {@link DRIVE.exitSpeed}. It is gone (owner decision, `HANDOFF.md` #5), and
 * what replaced it is the COAST: above that speed F is a BAIL, the player is
 * put down clear of the flank, and the car they let go of KEEPS ROLLING.
 *
 * The car cannot be handed back to `Traffic` to do that. An AI car is an arc
 * length on a lane (see `Traffic.js`) and a car the player was throwing round
 * a corner is nowhere near one. So this file keeps integrating it: the same
 * {@link stepCarBody}, the same {@link resolveCarBody}, the same bounds clamp
 * and the same fleet writes, with the throttle at zero and the free-rolling
 * drag swapped for a service decel ({@link DRIVE.coastBrake}). Below
 * {@link DRIVE.coastDoneSpeed} it goes through `traffic.returnParked` — the
 * SAME handover a stopped exit uses, so the slot ends up an ordinary
 * driverless parked car: colliders back, enterable again, and collectable by
 * the guest despawn ring if it came out of the reserve. One handover path, no
 * orphaned hold or pin.
 *
 * The coast keeps its OWN body, slot and handle rather than borrowing the
 * driven ones, because the player is on foot the instant they bail and can
 * walk straight into another car while the first is still slowing down.
 * `force` (death, teardown) is the opposite deal: it never coasts, because a
 * corpse's car rolling on driverless is a car the player wakes up chasing.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IMPACTS ARE REPORTED, NEVER RESOLVED TWICE
 * ─────────────────────────────────────────────────────────────────────────────
 * {@link resolveCarBody} and `bumpTraffic` are the only two places in the game
 * where the player's car meets something, so they are the only honest places to
 * measure it. Both write what the hit COST — see {@link CarBody.impactDv} — and
 * {@link createDriving}'s optional `onImpact` posts it onward. That is the whole
 * vehicle-damage seam from this side: no second collision test, no damage state
 * in here, no three.js, and nothing at all happens when no callback is passed. A
 * damage system that re-derived impacts from the velocity instead would have to
 * guess which of the two paths a loss came from, and would bill a handbrake stop
 * as a crash.
 *
 * @module engine/systems/Driving
 */

/** @typedef {import('../traffic/Traffic.js').Traffic} Traffic */

/**
 * Arcade tuning. Metres, seconds, radians.
 *
 * The lateral half of this table is DERIVED, not dialled: pick {@link gripLat}
 * and the steady turn radius (`v²/gripLat`), the lock curve and the yaw cap all
 * fall out of it, which is why there is no longer a hand-tabled lock ramp to
 * keep in sync with it. The longitudinal half — accel, brakes, drag, reverse —
 * is still settled by driving the city.
 */
export const DRIVE = Object.freeze({
  /** Peak drive force, m/s², at a standstill. See {@link DRIVE.accelFade}. */
  accel: 8.5,
  topSpeed: 17,
  /**
   * How the drive force falls away as {@link topSpeed} nears, as an exponent on
   * `v / topSpeed`. 2 is roughly aero drag and gives `v(t) = top·tanh(a·t/top)`
   * — the car surges away from rest and eases into its limiter. The old model
   * pulled a flat 9 m/s² and then hit a wall, which is what an electric
   * go-kart does, not a saloon. Raise it and the top end arrives sooner and
   * harder; drop it below ~1.4 and the last 3 m/s take forever.
   */
  accelFade: 2,
  /**
   * Service brake while moving forward. Below {@link gripLat} on purpose: the
   * brakes are the one control that can out-spend the tyres, and a car that
   * stops in 9 m from 60 km/h reads as hitting a wall rather than braking.
   */
  brakeDecel: 11,
  reverseAccel: 6,
  reverseTop: 6,
  /**
   * Coasting loss with no pedal — enough that letting go slows you visibly.
   * CONSTANT on purpose: {@link COAST_TUNE} swaps exactly this number for the
   * driverless service decel, and a speed-dependent drag would make the coast
   * distance stop being `v²/2a` (which `tests/carjack.test.mjs` section (q)
   * measures against the road).
   */
  drag: 2.2,
  /**
   * Handbrake decel, ON TOP of {@link drag}. Deliberately WEAK — it locks the
   * back wheels, it is not a second brake pedal. At the old 10 m/s² a
   * handbrake turn from 8 m/s had the car stopped in 0.8 s, before it could
   * rotate far enough to be a slide, so the tool the owner asked to keep was
   * really an emergency stop.
   */
  handbrakeDecel: 5.5,

  /**
   * How fast the wheels reach the commanded angle AT REST, and at
   * {@link topSpeed}. The gap is the weight in the steering: 0.22 s to full
   * lock in a car park, 0.43 s flat out. A single fast rate reads as twitchy,
   * which is the other half of what made the old car feel wrong.
   *
   * ⚠ `steerRateTop` was 1.7 (0.59 s to full lock). Owner playtest round 4:
   * "the player can barely turn the steering wheel at high speeds". A slow
   * wheel and a small lock and a tight yaw cap all read as the SAME complaint
   * from the driver's seat, so all three were eased together — see
   * {@link gripGain}. Keep the gap: a car whose wheel is as quick at 60 km/h
   * as it is in a car park has no mass.
   */
  steerRate: 4.6,
  steerRateTop: 2.2,
  /**
   * Returning to centre — and UNWINDING generally, including a full reversal.
   * Never speed-scaled: this is the counter-steer, and a slow one makes a
   * handbrake slide unsaveable.
   */
  steerReturn: 5.5,
  /**
   * Full lock is the angle whose geometry demands this multiple of the BASE
   * {@link gripLat} at the current speed, which makes the lock curve fall as
   * 1/v² instead of the old straight line. Above 1 so holding full lock flat
   * out always understeers a little; at 1 or below the car can never break
   * traction with steering alone and the whole grip budget stops being
   * visible; far above it the top of the stick is dead travel.
   */
  lockAuthority: 1.15,
  /**
   * Floor under that curve as a fraction of {@link STEER_MAX}. Never binds at
   * {@link topSpeed} (the curve lands near 0.22 there) — it is the guard that
   * stops a car somehow carrying more speed than its limiter from losing the
   * steering entirely.
   */
  lockFloor: 0.1,

  /**
   * THE TRACTION BUDGET: lateral tyre force, m/s². ~0.97 g — a touch above a
   * real road tyre, which is the arcade forgiveness. It sets the steady turn
   * radius directly (`R = v² / gripLat`, so 6.7 m at 8 m/s and 30 m at 17) and
   * is therefore the single biggest number in this file. Raise it and the city
   * turns into a kart track; drop it and a 90° junction needs walking pace.
   */
  gripLat: 9.5,
  /**
   * THE HIGH-SPEED PENALTY, SOFTENED — how much lateral budget the tyres gain
   * between {@link gripGainFrom} and {@link topSpeed}, as a fraction of
   * {@link gripLat}.
   *
   * ⚠ THIS IS NOT PHYSICS AND IT IS NOT AN ACCIDENT. A constant budget makes
   * the steady radius exactly `v²/gripLat`, which at 17 m/s is a 30 m circle —
   * correct, and (owner playtest round 4) far too heavy: a 90-degree junction
   * on a 54 m block cannot be taken at any speed worth carrying, so flat out
   * the car reads as unsteerable rather than as fast. 0.5 lifts the budget to
   * 14.25 m/s² at the limiter and pulls that circle in to ~20 m, which is a
   * corner a player can commit to and still not the 7 m one they get at
   * walking pace. The penalty survives — the radius still grows ~2.2x between
   * 9 and 15 m/s — it is just no longer the whole of the driving.
   *
   * The gain is folded into the BASE budget, which means it also moves the
   * steering lock (see {@link lockAuthority}): the lock is derived from the
   * base, so leaving it on the un-gained number would put the GEOMETRY back in
   * charge at speed and the pedals would stop changing the line — the one
   * failure this model exists to avoid. Move the two together or not at all.
   */
  gripGain: 0.5,
  /**
   * ...and where the gain starts, m/s. Deliberately at `v*` — the speed the
   * lock hands over to the grip cap, `sqrt(gripLat·wheelbase/tan(STEER_MAX))`
   * ≈ 7 — so the whole GEOMETRY regime below it is untouched and parking is
   * still `wheelbase/tan(STEER_MAX)` exactly. Re-derive it if `gripLat` or
   * `STEER_MAX` move; a knee below `v*` would quietly widen the parking
   * circle.
   */
  gripGainFrom: 7,
  /**
   * Below this speed the yaw cap `aLat / v` is not allowed to blow up, so
   * parking is pure geometry (radius `wheelbase / tan(STEER_MAX)`) and the
   * budget never touches it.
   */
  gripRefSpeed: 3,
  /** Extra lateral budget with the nose loaded — this is "brakes tighten the line". */
  brakeGrip: 0.22,
  /** ...and the budget lost with the tail squatted. Power out of a corner washes wide. */
  powerLoss: 0.18,
  /** How fast {@link CarBody.load} chases the pedals, 1/s. ~0.17 s of pitch lag. */
  loadRate: 6,
  /**
   * Lateral speed, m/s, at which a slide is judged to be spending the WHOLE of
   * {@link slideLoss} of the budget. Understeer that builds through a corner
   * and eases when you lift comes out of this pair, and the loop is stable
   * because it is negative feedback: more slide, less grip, less yaw, less
   * slide.
   */
  slideRef: 4,
  slideLoss: 0.35,

  /** Lateral velocity bleed, 1/s — the soft, small-slip half of the recovery. */
  grip: 10,
  /**
   * ...capped at this many m/s² of actual scrub. Without the cap the bleed
   * takes 5 g off a sideways car in a single step, which is the "infinite
   * grip" tell: the car snaps straight instead of gathering itself up.
   * Slightly above {@link gripLat} because a car that can never recover from a
   * slide faster than it can corner reads as ice.
   */
  gripRecover: 12,
  /** ...with the handbrake down. This gap IS the slide. */
  handbrakeGrip: 2.5,
  /**
   * ...and the handbrake lifts the yaw cap by this much at the same time. The
   * cap is a PATH-curvature limit, so without lifting it the car could never
   * rotate faster than its path bends — i.e. could never oversteer. This one
   * number is the difference between the deliberate slide and the natural
   * grip loss, which is understeer.
   */
  handbrakeYaw: 1.9,

  /** Door-to-player distance that counts as "beside the car". */
  enterRadius: 2.2,
  /**
   * The line between STEPPING OUT and BAILING OUT. At or below it the car is
   * parked where it stands, exactly as it always was; above it the player is
   * thrown clear and the car coasts (see the header). It is no longer a gate:
   * nothing refuses an exit at any speed.
   */
  exitSpeed: 3,
  /**
   * Service decel of a driverless car, m/s². Deliberately close to the
   * traffic planner's `curveBrake` and well under the driven {@link brakeDecel}
   * — it has to read as a car rolling to a stop with nobody in it, not as a
   * car standing on its own brakes.
   */
  coastBrake: 3.5,
  /** ...and below this the roll is over and the slot is handed back. */
  coastDoneSpeed: 0.3,
  /**
   * Extra metres out along the flank a BAILING player is put down, past the
   * `0.5 m` a stopped exit uses. The car is still moving and its rear wheel
   * sweeps past wherever they land.
   */
  bailClearance: 1.2,
  /** ...and how far back down the car, so they are not dropped on the sill. */
  bailBack: -0.6,
  /** Seconds between the request and the transfer. 0 today — see the header. */
  enterTime: 0,
  exitTime: 0,

  /** Body circles are the two axle midpoints at this share of the body width. */
  bodyRadius: 0.5,
  /** Speed lost per metre of collision penetration. */
  impactKill: 3,
  /** Radius of a traffic car treated as a single pushable circle. */
  trafficRadius: 1.55,
  /** Speed both cars lose in a shunt. */
  trafficKill: 0.45,
});

/**
 * {@link DRIVE} with the coast's service decel in place of the free-rolling
 * drag. ONE number differs, and swapping it is what lets a driverless car run
 * through the same integrator a driven one does instead of a second, drifting
 * copy of it: throttle 0 already spends `drag` per second along the heading and
 * cannot push the speed through zero, which is exactly the brake wanted here.
 */
const COAST_TUNE = Object.freeze({ ...DRIVE, drag: DRIVE.coastBrake });

/**
 * THE BASE lateral budget at a speed, m/s² — {@link DRIVE.gripLat} plus the
 * high-speed gain ({@link DRIVE.gripGain}), and nothing else. The pedals and
 * the slide scale THIS; the steering lock is derived from THIS. Exported
 * because a test that wants the steady radius at a speed must ask the same
 * question the model does, not re-hardcode `v²/gripLat`.
 *
 * @param {number} av Absolute speed along the heading, m/s.
 * @param {typeof DRIVE} [tune]
 * @returns {number}
 */
export function gripAt(av, tune = DRIVE) {
  const span = Math.max(1e-3, tune.topSpeed - tune.gripGainFrom);
  return tune.gripLat * (1 + tune.gripGain * clamp((av - tune.gripGainFrom) / span, 0, 1));
}

/**
 * One car body. Plain data so a test can make one without a level.
 * @typedef {Object} CarBody
 * @property {number} x @property {number} y @property {number} z
 * @property {number} yaw Radians; the nose points `(cos yaw, −sin yaw)`.
 * @property {number} vx @property {number} vz World velocity.
 * @property {number} speed Signed speed ALONG the heading (negative = reverse).
 * @property {number} steer −1..1, the SMOOTHED command (not the raw input, and
 *   not the angle — the speed-sensitive lock has not been applied yet).
 * @property {number} steerAngle Radians the model actually steered with, i.e.
 *   `steer * lock * STEER_MAX`. This is what the front wheels are drawn at.
 * @property {number} load Longitudinal weight transfer, −1 (squatted on the
 *   power) to +1 (nose down on the brakes). Smoothed; see {@link DRIVE.loadRate}.
 * @property {number} roll Wheel roll, radians — only used headless.
 * @property {number} impactDepth Metres of static geometry {@link resolveCarBody}
 *   pushed this body out of on the LAST step it ran, 0 on a clean one.
 * @property {number} impactDv ...and the metres per second that resolution
 *   actually took off the body: the normal-velocity component killed PLUS the
 *   depth scrub. THIS is the honest impulse measure and the depth is not — a car
 *   creeping into a wall at walking pace can post a big depth and lose almost
 *   nothing, and a car that clips a kerb at 17 m/s posts a tiny one and loses
 *   half its speed. Anything reading the depth as "how hard was that" reads it
 *   backwards.
 * @property {{wheelbase: number, L: number, W: number, wheelR: number,
 *   fa: number, ra: number}} chassis
 */

/**
 * @param {any} [chassis]
 * @returns {CarBody}
 */
export function newCarBody(chassis = null) {
  return {
    x: 0,
    y: 0,
    z: 0,
    yaw: 0,
    vx: 0,
    vz: 0,
    speed: 0,
    steer: 0,
    steerAngle: 0,
    load: 0,
    roll: 0,
    impactDepth: 0,
    impactDv: 0,
    chassis: chassis ?? { wheelbase: 2.7, L: 4.7, W: 1.8, wheelR: 0.33, fa: 1.45, ra: -1.25 },
  };
}

/**
 * One fixed step of the bicycle model. PURE: it touches nothing but `body`, and
 * `tests/traffic.test.mjs` drives it directly.
 *
 * @param {CarBody} body Mutated.
 * @param {{throttle: number, steer: number, handbrake: boolean}} ctrl
 *   `throttle` +1 accelerate / −1 brake-then-reverse; `steer` +1 LEFT.
 * @param {number} dt
 * @param {typeof DRIVE} [tune]
 */
export function stepCarBody(body, ctrl, dt, tune = DRIVE) {
  if (!(dt > 0)) return;
  const wb = Math.max(1, body.chassis.wheelbase);

  // ── heading before anything moves ─────────────────────────────────────────
  const fx0 = Math.cos(body.yaw);
  const fz0 = -Math.sin(body.yaw);
  const vf = body.vx * fx0 + body.vz * fz0;
  const av = Math.abs(vf);
  const th = ctrl.throttle ?? 0;

  // ── 1. weight transfer ────────────────────────────────────────────────────
  // One smoothed number for the whole pitch axis. `braking` deliberately means
  // the SERVICE BRAKE and not reverse: holding S at a standstill is a driver
  // backing out of a bay, and it must not load a nose that is going nowhere.
  const braking = th < 0 && vf > 0.2;
  const loadWant = braking ? 1 : th > 0 ? -Math.min(1, th) : 0;
  // `|| 0` because this is an exported entry point and a body handed in from a
  // test or a save that predates the field would otherwise turn the whole
  // integrator to NaN on its first step, silently, one frame before the car
  // disappears.
  const load0 = body.load || 0;
  body.load = load0 + (loadWant - load0) * (1 - Math.exp(-tune.loadRate * dt));

  // ── 2. the traction budget ────────────────────────────────────────────────
  // What the tyres can spend sideways THIS step. Load first (the pedals), then
  // whatever a slide already in progress is using up — the second term is what
  // makes understeer build through a corner rather than being a constant.
  const lat0 = body.vx * -fz0 + body.vz * fx0;
  const slide = Math.min(1, Math.abs(lat0) / tune.slideRef);
  const loadScale = 1 + body.load * (body.load > 0 ? tune.brakeGrip : tune.powerLoss);
  // `gripAt` is the BASE at this speed — flat `gripLat` up to `v*`, then the
  // softened high-speed penalty (see {@link DRIVE.gripGain}). The two scales
  // below are the ±15% band the pedals and a slide move the car through, and
  // they must never be folded into the base: the lock is derived from the base
  // and would then chase its own tail.
  const gripBase = gripAt(av, tune);
  const aLat = gripBase * loadScale * (1 - tune.slideLoss * slide);

  // ── 3. steering: the command, then the lock derived from the BASE budget ──
  const want = clamp(ctrl.steer ?? 0, -1, 1);
  // Unwinding — releasing, or throwing the wheel the other way — is always the
  // fast rate. Winding ON gets heavier with speed.
  const unwinding = want === 0 || want * body.steer < 0 || Math.abs(want) < Math.abs(body.steer);
  const rate = unwinding
    ? tune.steerReturn
    : tune.steerRate + (tune.steerRateTop - tune.steerRate) * Math.min(1, av / tune.topSpeed);
  body.steer = approach(body.steer, want, rate * dt);
  // `v² · tan(δ) / wheelbase` is the lateral accel the geometry asks for, so
  // the angle that asks for exactly `lockAuthority · gripBase` is
  // `atan(lockAuthority · gripBase · wb / v²)`. At v = 0 that is atan(∞) = π/2,
  // which the clamp turns into full lock — no branch, no divide-by-zero.
  const lock = clamp(
    Math.atan((tune.lockAuthority * gripBase * wb) / (av * av)) / STEER_MAX,
    tune.lockFloor,
    1,
  );
  const steerAngle = body.steer * lock * STEER_MAX;
  body.steerAngle = steerAngle;

  // ── 4. longitudinal impulse, along the OLD heading ────────────────────────
  let dvf;
  if (th > 0) {
    // Drive force falls away toward the limiter instead of stopping at a wall.
    const head = 1 - Math.pow(Math.min(1, Math.max(0, vf) / tune.topSpeed), tune.accelFade);
    dvf = Math.min(tune.accel * th * head * dt, Math.max(0, tune.topSpeed - vf));
  } else if (th < 0) {
    dvf = vf > 0.2
      ? -Math.min(tune.brakeDecel * dt, vf)
      : -Math.min(tune.reverseAccel * dt, Math.max(0, vf + tune.reverseTop));
  } else {
    dvf = -Math.sign(vf) * Math.min(Math.abs(vf), tune.drag * dt);
  }
  if (ctrl.handbrake) dvf -= Math.sign(vf + dvf) * Math.min(Math.abs(vf + dvf), tune.handbrakeDecel * dt);
  body.vx += fx0 * dvf;
  body.vz += fz0 * dvf;

  // ── 5. yaw: what the geometry asks for, capped by what the tyres can hold ─
  // `aLat / v` is the fastest the tyres can bend the PATH at this speed, and
  // above ~7 m/s it is always the binding constraint — that is the understeer,
  // and it is why the steady radius is v²/aLat rather than wheelbase/tan(δ).
  // The handbrake lifts the cap because a locked rear axle lets the car rotate
  // further than its path bends; that, plus the lateral bleed dropping below,
  // is the slide.
  const yawDemand = (vf / wb) * Math.tan(steerAngle);
  const yawCap = (aLat * (ctrl.handbrake ? tune.handbrakeYaw : 1)) / Math.max(tune.gripRefSpeed, av);
  body.yaw += clamp(yawDemand, -yawCap, yawCap) * dt;

  // ── 6. grip: bleed the lateral component of the NEW heading ───────────────
  const fx1 = Math.cos(body.yaw);
  const fz1 = -Math.sin(body.yaw);
  const rx = -fz1;
  const rz = fx1;
  const lat = body.vx * rx + body.vz * rz;
  // Soft for small slip, but capped in absolute scrub: an uncapped exponential
  // straightens a sideways car at several g and reads as the velocity being
  // welded to the heading.
  const cap = tune.gripRecover * dt;
  const shed = clamp(lat * (1 - Math.exp(-(ctrl.handbrake ? tune.handbrakeGrip : tune.grip) * dt)), -cap, cap);
  body.vx -= rx * shed;
  body.vz -= rz * shed;

  // ── 7. integrate ──────────────────────────────────────────────────────────
  body.x += body.vx * dt;
  body.z += body.vz * dt;
  body.speed = body.vx * fx1 + body.vz * fz1;
  body.roll -= (body.speed * dt) / Math.max(0.1, body.chassis.wheelR);
}

/**
 * Pushes a car body out of static geometry. TWO circles at the axle midpoints:
 * one circle at the centre lets a 5 m pickup post its nose through a wall, and
 * a real oriented box needs a solver this game does not have.
 *
 * IT ALSO RECORDS WHAT THE HIT COST, on the body and nowhere else:
 * {@link CarBody.impactDepth} and {@link CarBody.impactDv}, rewritten EVERY call
 * and zeroed on a clean one so a reader can never mistake last second's crash
 * for this step's. They are pure output — nothing in this file reads them back —
 * which is what lets a damage system live outside the integrator without a
 * second copy of the collision test. The RETURN VALUE and the signature are
 * unchanged on purpose: `tests/traffic.test.mjs` section (l) pins both.
 *
 * @param {CarBody} body Mutated.
 * @param {any} collision `CollisionGrid`, or null.
 * @param {{x: number, z: number}} probe Scratch — must be reused, not allocated.
 * @returns {number} Penetration depth resolved, metres. 0 for a clean step.
 */
export function resolveCarBody(body, collision, probe) {
  // Zeroed FIRST, before every early return: "no grid" and "missed everything"
  // are both clean steps, and leaving a stale reading on either is how a car
  // that hit a wall once goes on taking damage for the rest of the level.
  body.impactDepth = 0;
  body.impactDv = 0;
  if (!collision || typeof collision.resolveCircle !== 'function') return 0;
  const ch = body.chassis;
  const r = ch.W * DRIVE.bodyRadius;
  const c = Math.cos(body.yaw);
  const sn = Math.sin(body.yaw);
  let px = 0;
  let pz = 0;
  let depth = 0;

  for (let k = 0; k < 2; k++) {
    const ax = k === 0 ? ch.fa : ch.ra;
    probe.x = body.x + ax * c;
    probe.z = body.z - ax * sn;
    const res = collision.resolveCircle(probe, r, 2);
    if (!res.hit) continue;
    const dx = res.x - probe.x;
    const dz = res.z - probe.z;
    px += dx;
    pz += dz;
    const d = Math.hypot(dx, dz);
    if (d > depth) depth = d;
  }
  if (depth <= 0) return 0;

  body.x += px;
  body.z += pz;
  // Kill the velocity going INTO the wall, then scrub the rest by the depth —
  // the slide is what stops a glancing kerb strike from stopping the car dead.
  //
  // `dv` accumulates the same two terms IN THE SAME ORDER, which is what makes
  // it the honest impulse: the normal component is what the wall took head-on,
  // and the scrub is what the depth took off whatever was left of the slide. A
  // glancing kerb at speed therefore reports a small `dv` and a big `depth`;
  // driving square into a building reports the opposite.
  let dv = 0;
  const l = Math.hypot(px, pz);
  if (l > 1e-6) {
    const nx = px / l;
    const nz = pz / l;
    const vn = body.vx * nx + body.vz * nz;
    if (vn < 0) {
      body.vx -= nx * vn;
      body.vz -= nz * vn;
      dv -= vn;
    }
  }
  const kill = clamp(depth * DRIVE.impactKill, 0, 1);
  // Measured AFTER the normal kill, so the two terms cannot double-count the
  // speed the wall already took.
  dv += Math.hypot(body.vx, body.vz) * kill;
  body.vx *= 1 - kill;
  body.vz *= 1 - kill;
  body.impactDepth = depth;
  body.impactDv = dv;
  return depth;
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * The driving system. Owns the player's car slot, the enter/exit handshake and
 * the camera mode; owns no GPU resource at all.
 *
 * @param {Object} o
 * @param {Traffic} o.traffic
 * @param {any} o.player {@link import('../actors/Player.js').Player}
 * @param {any} o.rig {@link import('../actors/CameraRig.js').CameraRig}
 * @param {any} o.input
 * @param {any} [o.collision]
 * @param {(x: number, z: number) => number} [o.heightAt]
 * @param {any} [o.bus]
 * @param {import('../types.js').Bounds2|null} [o.bounds]
 * @param {() => number} [o.getNight]
 * @param {((kind: 'wall'|'car', dv: number, handle: number, otherIdx: number)
 *   => void)|null} [o.onImpact] THE DAMAGE SEAM, and it is a REPORT rather than
 *   a hook: this file resolves the collision exactly as it always did and then
 *   says what it cost. `kind` is `'wall'` for static geometry (`otherIdx` −1) or
 *   `'car'` for a traffic shunt (`otherIdx` is the DRIVING slot that was
 *   shoved); `dv` is metres per second and is never 0 — a clean step reports
 *   nothing at all. `handle` is the FLEET handle of the car that took it, which
 *   is the driven one or the COASTING one, never "the player's" (the player can
 *   be in a second car while the first is still rolling through a bus stop).
 *
 *   Absent — every test, every level with no damage system — and NOTHING in
 *   this file changes: the `?.` is the whole gate, the same inert-at-default
 *   idiom `speedMul: 1` and `Player.armour: 0` use.
 * @returns {Object}
 */
export function createDriving(o) {
  const {
    traffic,
    rig = null,
    input = null,
    collision = null,
    heightAt = () => 0,
    bus = null,
    bounds = null,
    getNight = null,
    onImpact = null,
  } = o ?? {};
  // Rebindable on purpose: Gameplay hot-swaps the Player once its sprite
  // decodes, and a captured-by-value reference would go on steering a
  // disposed actor (see the swap block in Gameplay.js).
  let player = o?.player;

  if (!traffic) throw new Error('createDriving: a Traffic director is required');

  const body = newCarBody();
  /**
   * `'foot' | 'entering' | 'car' | 'exiting'` — the PLAYER's phase, and only
   * theirs. A coasting car is deliberately NOT a fifth value: the player is on
   * FOOT while it rolls (they can walk, they can be shot, they can get into
   * the next car along), and `Carjack#press` reads this to decide whether F is
   * an entry. A `'coasting'` phase would silently kill F for four seconds.
   */
  let phase = 'foot';
  let slot = -1;
  let handle = -1;
  let door = { x: 0, z: 0 };
  let timer = 0;
  /** Set by {@link exitCar}: this exit is a bail, so {@link finishExit} coasts. */
  let bail = false;
  const probe = { x: 0, z: 0 };
  const ctrl = { throttle: 0, steer: 0, handbrake: false };
  const lamps = { head: 0, brake: 0, sigL: 0, sigR: 0 };
  let disposed = false;

  // ── the coast: a car the player bailed out of, still rolling ──────────────
  // Its OWN body, slot and handle, never the driven ones — the player is on
  // foot from the step they bail and may be in another car before this one
  // stops. ONE coast at a time: a second bail parks the first where it stands,
  // and by then it has been braking for however long the player spent getting
  // into the next car. Two rolling ghosts would be a second fleet.
  /** @type {CarBody|null} */
  let coastBody = null;
  let coastSlot = -1;
  let coastHandle = -1;
  const coastCtrl = { throttle: 0, steer: 0, handbrake: false };
  const coastLamps = { head: 0, brake: 0, sigL: 0, sigR: 0 };
  const coastProbe = { x: 0, z: 0 };

  /** @param {...string} codes @returns {boolean} */
  const held = (...codes) => {
    if (!input) return false;
    for (const c of codes) if (input.down(c)) return true;
    return false;
  };

  /** @returns {boolean} */
  const inCar = () => phase === 'car';

  // ── enter ─────────────────────────────────────────────────────────────────

  /**
   * The F key. Enters the nearest parked car, or leaves the current one.
   * @returns {boolean} True when something happened.
   */
  const toggle = () => (inCar() ? exitCar() : requestEnter());

  /** @returns {boolean} */
  const requestEnter = () => {
    if (phase !== 'foot') return false;
    const idx = traffic.nearestEnterable(player.position.x, player.position.z, DRIVE.enterRadius);
    if (idx < 0) return false;
    return enterCar(idx);
  };

  /**
   * Begins the transfer. THE CARJACK SEAM — see the header: after this call the
   * car belongs to the player and to nobody else, but they are not driving it
   * until `_finishEnter`.
   *
   * `delay` is the gap between the two, i.e. how long the slide-in animation
   * gets. It defaults to {@link DRIVE.enterTime} (0 — request and completion
   * back to back, which is what a caller with no animation wants) and
   * `systems/Carjack.js` passes its own, so the choreography's timings live in
   * ONE table beside the rest of the sequence rather than half here.
   *
   * @param {number} idx Parked slot.
   * @param {number} [delay=DRIVE.enterTime] Seconds before `finishEnter`.
   * @returns {boolean}
   */
  const enterCar = (idx, delay = DRIVE.enterTime) => {
    if (phase !== 'foot') return false;
    const taken = traffic.takeParked(idx);
    if (!taken) return false;
    slot = idx;
    handle = taken.handle;
    door = taken.door ?? { x: 0, z: 0 };
    body.chassis = taken.chassis;
    body.x = taken.x;
    body.y = taken.y;
    body.z = taken.z;
    body.yaw = taken.yaw;
    body.vx = 0;
    body.vz = 0;
    body.speed = 0;
    body.steer = 0;
    body.steerAngle = 0;
    body.load = 0;
    phase = 'entering';
    timer = delay > 0 ? delay : 0;
    if (timer <= 0) finishEnter();
    return true;
  };

  const finishEnter = () => {
    phase = 'car';
    timer = 0;
    player.setVisible?.(false);
    player.carry?.(body.x, heightAt(body.x, body.z), body.z, 0, 0);
    rig?.setDriveMode?.(true);
    traffic.setPedestrian(0, 0, false);
    traffic.setPlayerCar(body.x, body.z, true);
    bus?.emit?.('player:vehicle', { in: true });
  };

  // ── exit ──────────────────────────────────────────────────────────────────

  /**
   * THE SPEED GATE IS GONE (owner decision, `HANDOFF.md` #5): this never
   * refuses on speed any more. Above {@link DRIVE.exitSpeed} it is a BAIL —
   * the player is put down clear of the flank and the car coasts on driverless
   * (see the header) — and at or below it nothing has changed.
   *
   * @param {boolean} [force=false] Park the car WHERE IT STANDS whatever the
   *   speed, and never coast it. Death and teardown pass true: a corpse's car
   *   rolling on by itself is a car the player wakes up chasing.
   * @param {number} [delay=DRIVE.exitTime] Seconds before `finishExit`, i.e.
   *   how long the climb-out animation gets. Same seam as {@link enterCar}. A
   *   bail passes 0 — there is no climb out of a car you jumped from.
   * @returns {boolean} False only when there is no car to leave.
   */
  const exitCar = (force = false, delay = DRIVE.exitTime) => {
    if (phase !== 'car') return false;
    bail = !force && Math.abs(body.speed) > DRIVE.exitSpeed;
    phase = 'exiting';
    timer = delay > 0 ? delay : 0;
    if (timer <= 0) finishExit();
    return true;
  };

  const finishExit = () => {
    const c = Math.cos(body.yaw);
    const sn = Math.sin(body.yaw);
    // Step out level with the driver's door, one body-width clear of the flank.
    // A BAIL goes further: `bailClearance` more out along the flank and
    // `bailBack` down the car, because the thing they just left is still moving
    // and its rear wheel sweeps past wherever they land. That clearance IS the
    // whole "post-exit grace" — a coasting car carries no colliders (they went
    // with `takeParked` and come back with `returnParked`) and `bumpTraffic`
    // only ever pushes the DRIVEN body, so there is no contact to time out,
    // only a silhouette to stay out of.
    const out = door.z < 0 ? -1 : 1;
    const lx = door.x + (bail ? DRIVE.bailBack : 0);
    const lz = door.z + out * (0.5 + (bail ? DRIVE.bailClearance : 0));
    let px = body.x + lx * c + lz * sn;
    let pz = body.z - lx * sn + lz * c;
    if (collision && typeof collision.resolveCircle === 'function') {
      probe.x = px;
      probe.z = pz;
      const res = collision.resolveCircle(probe, player.radius ?? 0.35, 2);
      px = res.x;
      pz = res.z;
    }

    // Reads `slot`/`handle`, so it goes before they are cleared.
    if (bail) startCoast();
    else traffic.returnParked(slot, body.x, heightAt(body.x, body.z), body.z, body.yaw);
    bail = false;
    slot = -1;
    handle = -1;
    phase = 'foot';
    timer = 0;
    // The state migrated to the coast (or to a parked slot); leaving stale
    // velocity on the driven body would make `stats()` lie until the next
    // entry zeroes it.
    body.vx = 0;
    body.vz = 0;
    body.speed = 0;
    body.steer = 0;
    body.steerAngle = 0;
    body.load = 0;
    player.setVisible?.(true);
    player.moveTo(px, pz);
    rig?.setDriveMode?.(false);
    // A coasting car is still an obstacle the AI brakes for, so it KEEPS the
    // player-car actor slot until it parks.
    if (!coastBody) traffic.setPlayerCar(0, 0, false);
    traffic.setPedestrian(player.position.x, player.position.z, true);
    bus?.emit?.('player:vehicle', { in: false });
  };

  // ── the coast ─────────────────────────────────────────────────────────────

  /**
   * Hands the car the player just jumped out of over to the coast. Its state is
   * COPIED, not aliased: the driven `body` is published on the system object
   * and is about to be reset, and the roll has to carry on from exactly the
   * velocity, yaw and wheel angle the last driven step left.
   */
  const startCoast = () => {
    if (coastBody) parkCoast();
    const b = newCarBody(body.chassis);
    b.x = body.x;
    b.y = body.y;
    b.z = body.z;
    b.yaw = body.yaw;
    b.vx = body.vx;
    b.vz = body.vz;
    b.speed = body.speed;
    b.steer = body.steer;
    b.steerAngle = body.steerAngle;
    // The pitch the car was carrying when it was let go of. Copying it rather
    // than starting from 0 is what stops a car bailed from under braking
    // gaining a step of extra grip on the frame it changes hands.
    b.load = body.load;
    b.roll = body.roll;
    coastBody = b;
    coastSlot = slot;
    coastHandle = handle;
  };

  /**
   * The roll is over. `returnParked` is the SAME handover a stopped exit uses,
   * so the slot becomes an ordinary driverless parked car — colliders back,
   * enterable again, and recycled by the guest despawn ring if it came out of
   * the reserve. One handover path is the whole point: two of them is how the
   * player's car ends up existing twice.
   */
  const parkCoast = () => {
    if (!coastBody) return;
    const b = coastBody;
    traffic.returnParked(coastSlot, b.x, heightAt(b.x, b.z), b.z, b.yaw);
    coastBody = null;
    coastSlot = -1;
    coastHandle = -1;
    // A car the player is IN owns the obstacle slot and re-asserts it further
    // down this same step (see `update`), so this can never blank it.
    if (phase !== 'car' && phase !== 'exiting') traffic.setPlayerCar(0, 0, false);
  };

  /** @param {number} dt */
  const stepCoast = (dt) => {
    if (!coastBody) return;
    const b = coastBody;
    // Throttle 0 is the free-rolling coast the driven car already has;
    // COAST_TUNE swaps its drag for the service decel so the car stops in a
    // street rather than a district. Steer 0 lets `steerReturn` straighten the
    // wheels, which is why a car bailed mid-corner drifts out of the turn
    // instead of circling the block.
    coastCtrl.steer = 0;
    stepCarBody(b, coastCtrl, dt, COAST_TUNE);
    resolveCarBody(b, collision, coastProbe);
    // A driverless car still crashes, and it crashes as ITSELF: the report
    // carries `coastHandle`, not the driven one. The player may already be in
    // the next car along, and billing them for this one's wall would put the
    // smoke on the wrong bonnet.
    if (b.impactDv > 0) onImpact?.('wall', b.impactDv, coastHandle, -1);
    if (bounds) {
      b.x = clamp(b.x, bounds.minX + 1, bounds.maxX - 1);
      b.z = clamp(b.z, bounds.minZ + 1, bounds.maxZ - 1);
    }
    b.y = heightAt(b.x, b.z);

    const fleet = traffic.fleet;
    if (fleet && coastHandle >= 0) {
      fleet.setCarTransform(coastHandle, b.x, b.y, b.z, b.yaw);
      fleet.setSteer(coastHandle, b.steerAngle / STEER_MAX);
      fleet.advance(coastHandle, b.speed * dt);
      coastLamps.head = getNight && getNight() > 0.35 ? 1 : 0;
      // Brake lamps for the whole roll. It IS decelerating, and from behind
      // they are the only tell the player has that the car is coming to rest
      // rather than driving off with their getaway in it.
      coastLamps.brake = 1;
      fleet.setLamps(coastHandle, coastLamps);
    }
    // Still an obstacle the AI brakes for. It does not SHOVE them — that is
    // `bumpTraffic`, and it belongs to the driven body.
    traffic.setPlayerCar(b.x, b.z, true);

    if (Math.abs(b.speed) < DRIVE.coastDoneSpeed) parkCoast();
  };

  // ── step ──────────────────────────────────────────────────────────────────

  /**
   * One fixed step of the PLAYER's half of the traffic world. Call it in place
   * of `player.update(dt)` when {@link inCar} — the player is cargo then, not a
   * mover.
   * @param {number} dt
   */
  const update = (dt) => {
    if (disposed || !(dt > 0)) return;

    // The coast runs FIRST and belongs to no phase: while a car rolls the
    // player may be on foot, walking into another one, or already driving it.
    // Going first is also what lets the driven car below re-assert the single
    // player-car obstacle slot on the step a coast gives it up.
    stepCoast(dt);

    if (phase === 'entering' || phase === 'exiting') {
      timer -= dt;
      if (timer <= 0) (phase === 'entering' ? finishEnter : finishExit)();
      return;
    }
    if (phase !== 'car') {
      traffic.setPedestrian(player.position.x, player.position.z, !player.dead);
      return;
    }

    ctrl.throttle = (held('KeyW', 'ArrowUp') ? 1 : 0) - (held('KeyS', 'ArrowDown') ? 1 : 0);
    // A is LEFT, and left is a POSITIVE steer in this yaw convention.
    ctrl.steer = (held('KeyA', 'ArrowLeft') ? 1 : 0) - (held('KeyD', 'ArrowRight') ? 1 : 0);
    ctrl.handbrake = held('Space');

    const wasSpeed = body.speed;
    stepCarBody(body, ctrl, dt);
    resolveCarBody(body, collision, probe);
    // ONE wall report per body per step, and it goes BEFORE `bumpTraffic` so a
    // car that hits a wall and clips a taxi in the same step reports both, in
    // the order they happened.
    if (body.impactDv > 0) onImpact?.('wall', body.impactDv, handle, -1);
    bumpTraffic(dt);

    if (bounds) {
      body.x = clamp(body.x, bounds.minX + 1, bounds.maxX - 1);
      body.z = clamp(body.z, bounds.minZ + 1, bounds.maxZ - 1);
    }
    body.y = heightAt(body.x, body.z);

    // ── the fleet ──────────────────────────────────────────────────────────
    const fleet = traffic.fleet;
    if (fleet && handle >= 0) {
      fleet.setCarTransform(handle, body.x, body.y, body.z, body.yaw);
      // The front wheels AND the steering wheel come off this one number — an
      // owner requirement, and the tell that the sim is really driving the art.
      // It is the APPLIED angle, not the raw command: the speed-sensitive lock
      // takes full lock down to ~22% at 17 m/s, and wheels cranked to the stop
      // on a car tracing a 30 m arc is the sim and the art disagreeing.
      fleet.setSteer(handle, body.steerAngle / STEER_MAX);
      fleet.advance(handle, body.speed * dt);
      const night = getNight ? getNight() : 0;
      lamps.head = night > 0.35 ? 1 : 0;
      lamps.brake = ctrl.throttle < 0 || body.speed < wasSpeed - 0.08 ? 1 : lamps.head ? 0.5 : 0;
      fleet.setLamps(handle, lamps);
    }

    // The player rides along: position for the camera and the minimap, velocity
    // for the rig's look-ahead. `carry` is Player's hook for exactly this.
    player.carry?.(body.x, body.y, body.z, body.vx, body.vz);
    traffic.setPlayerCar(body.x, body.z, true);
  };

  /**
   * Circle-circle against every ACTIVE traffic car. Only the player moves — an
   * AI car is an arc length on a rail and cannot be shoved off it — but both
   * lose speed, so a shunt costs the shunter too.
   *
   * The shunt is reported ONCE PER CAR SHOVED (not once per step): running
   * through a queue at a junction really is several collisions, and collapsing
   * them into one would let a player scythe down a whole row for the price of
   * the first bumper. `dv` is the speed BOTH cars lost added together — the
   * closing rate the shunt actually spent — because `kill` is a shared FRACTION
   * and each car's own loss on its own says nothing about how hard they met.
   * @param {number} dt
   */
  const bumpTraffic = (dt) => {
    void dt;
    const r = DRIVE.trafficRadius + body.chassis.L * 0.32;
    for (let i = 0; i < traffic.top; i++) {
      if (!traffic.isDriving(i)) continue;
      const dx = body.x - traffic.x[i];
      const dz = body.z - traffic.z[i];
      const d2 = dx * dx + dz * dz;
      if (d2 >= r * r || d2 < 1e-9) continue;
      const d = Math.sqrt(d2);
      const push = r - d;
      body.x += (dx / d) * push;
      body.z += (dz / d) * push;
      const kill = clamp(push * DRIVE.trafficKill, 0, 0.6);
      // Both speeds BEFORE the kill, or the report measures what is left rather
      // than what went.
      const dv = (Math.hypot(body.vx, body.vz) + traffic.v[i]) * kill;
      body.vx *= 1 - kill;
      body.vz *= 1 - kill;
      traffic.v[i] *= 1 - kill;
      if (dv > 0) onImpact?.('car', dv, handle, i);
    }
  };

  return {
    name: 'driving',
    body,
    get inCar() {
      return inCar();
    },
    get phase() {
      return phase;
    },
    /** Parked slot the player's car came from; −1 on foot. */
    get carSlot() {
      return slot;
    },
    /**
     * FLEET handle of the car the player is in — the id every renderer, the
     * fleet and the damage system knows a car by.
     *
     * ⚠ IT IS LIVE ACROSS THE WHOLE TWO-PHASE SEAM, not just while `inCar`.
     * `enterCar` writes it at the REQUEST (the car belongs to the player from
     * that instant — see the header) and `finishExit` clears it at the very end
     * of the exit, so it answers through `'entering'` and `'exiting'` alike and
     * pairs with `carSlot`, which has exactly the same lifetime. Anything that
     * has to name the player's car mid-animation — a bullet that hits it during
     * the slide-in, a wreck that lands during the climb out — must read this
     * rather than gate on `inCar` and drop the event.
     */
    get fleetHandle() {
      return handle;
    },
    /** @returns {boolean} A car the player bailed out of is still rolling. */
    get coasting() {
      return !!coastBody;
    },
    /**
     * The coasting car's body, or null. Published for the HUD, the debug
     * overlay and `tests/carjack.test.mjs`; treat it as read-only.
     * @returns {CarBody|null}
     */
    get coastBody() {
      return coastBody;
    },
    /** @returns {number} Parked slot the coasting car will return to, or −1. */
    get coastSlot() {
      return coastSlot;
    },
    /**
     * @returns {number} FLEET handle of the coasting car, or −1. It is a
     * SEPARATE handle from {@link fleetHandle} and never the same one: the
     * player is on foot the instant they bail and may be driving another car
     * before this one stops, which is the whole reason the coast keeps its own
     * body, slot and handle.
     */
    get coastHandle() {
      return coastHandle;
    },
    toggle,
    requestEnter,
    enterCar,
    exitCar,
    update,
    /** Gameplay's player hot-swap points us at the replacement actor. */
    setPlayer(p) {
      player = p;
    },
    /**
     * @returns {{inCar: boolean, speed: number, kph: number, slot: number,
     *   coasting: boolean}}
     */
    stats() {
      return {
        inCar: inCar(),
        speed: body.speed,
        kph: Math.round(Math.abs(body.speed) * 3.6),
        slot,
        coasting: !!coastBody,
      };
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      // A car mid-roll goes back to the director rather than vanishing with
      // the system — same reason as the driven one below.
      parkCoast();
      if (phase === 'car' || phase === 'entering') {
        // Leave the world consistent: the car goes back to the director rather
        // than vanishing with the system.
        traffic.returnParked(slot, body.x, body.y, body.z, body.yaw);
        player.setVisible?.(true);
        rig?.setDriveMode?.(false);
      }
      phase = 'foot';
      slot = -1;
      handle = -1;
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────

/** @param {number} v @param {number} target @param {number} step */
function approach(v, target, step) {
  const d = target - v;
  if (Math.abs(d) <= step) return target;
  return v + Math.sign(d) * step;
}

/** @param {number} v @param {number} a @param {number} b */
function clamp(v, a, b) {
  return v < a ? a : v > b ? b : v;
}
